import { useEffect, useRef, useState } from "react";
import { useLazyGetCollectionsSummaryQuery } from "../collections/collectionsApi";
import { useLazyListOrdersQuery } from "../sales/salesApi";

// Collections only became the system of record in September 2026 - an order
// older than that may well have been paid before any receipt was entered here,
// so it would show as outstanding forever. Same cut-off as the guarded-customers card.
const SINCE = "2026-09-01";
const PAGE_SIZE = 100;
const MAX_PAGES = 20;
const INSTALLATION_SERVICE_TYPE = 1; // Cheque_Collection.Colect_Type: 0=Services 1=Installation 2=Rent 3=Alarms 4=Other

const num = (v) => Number(v) || 0;
const isoToday = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

/**
 * The order's one-time share, VAT and discount included. Guards and alarms are
 * monthly charges and must never land in this figure; only the OTHER-kind lines
 * (new installations) are billed once. TotalAmount is already net of discount
 * and gross of VAT, but it covers the whole order - so the one-time part is
 * taken as its proportion of the pre-VAT line amounts, which is exact for a
 * pure installation order and a fair split for a mixed one.
 */
const oneTimeValue = (o) => {
  const other = num(o.otherServicesAmount);
  if (other <= 0) return 0;
  const base = other + num(o.guardsAmount) + num(o.alarmsAmount) + num(o.tempGuardsAmount);
  return base > 0 ? (num(o.totalAmount) * other) / base : other;
};

/**
 * New-installation orders against what has been collected for them, per
 * customer - the one-time side of "expected vs collected". A receipt carries a
 * customer, never an order, so the comparison can only be honest per customer.
 *
 * "Collected" counts ONLY receipts tagged Installation. Every other receipt
 * from the same customer is returned separately (otherReceipts) rather than
 * added in: an untagged installation payment and a monthly guarding payment
 * look identical, and guessing would put monthly money against a one-time bill.
 */
export const useInstallationOrderPayments = ({ branchId, skip = false } = {}) => {
  const [state, setState] = useState({ customers: [], unlinked: { count: 0, amount: 0 }, isLoading: false, isError: false });
  const [fetchOrders] = useLazyListOrdersQuery();
  const [fetchSummary] = useLazyGetCollectionsSummaryQuery();
  const requestId = useRef(0);

  useEffect(() => {
    if (skip) return;
    const thisRequest = ++requestId.current;

    (async () => {
      setState((s) => ({ ...s, isLoading: true, isError: false }));
      try {
        const range = { branchId, dateFrom: SINCE, dateTo: isoToday() };
        const orders = [];
        for (let page = 1; page <= MAX_PAGES; page += 1) {
          const result = await fetchOrders({ ...range, view: "ALL", page, pageSize: PAGE_SIZE }, true).unwrap();
          const items = result.items || [];
          orders.push(...items);
          if (items.length === 0 || orders.length >= result.totalCount) break;
        }
        const [installationReceipts, allReceipts] = await Promise.all([
          fetchSummary({ ...range, groupBy: "customer", serviceType: INSTALLATION_SERVICE_TYPE }, true).unwrap(),
          fetchSummary({ ...range, groupBy: "customer" }, true).unwrap(),
        ]);
        if (thisRequest !== requestId.current) return; // a newer request superseded this one

        const byCustomer = new Map();
        const unlinked = { count: 0, amount: 0 };
        orders.forEach((o) => {
          const value = oneTimeValue(o);
          if (o.isCancelled || value <= 0) return;
          if (o.custId == null) {
            // An order for a prospect not yet in Customer Master - no receipt can
            // be entered against it, so it is counted, not silently dropped.
            unlinked.count += 1;
            unlinked.amount += value;
            return;
          }
          const key = `${o.branchId}-${o.custId}`;
          if (!byCustomer.has(key)) {
            byCustomer.set(key, { key, branchId: o.branchId, branchCode: o.branchCode, custId: o.custId, custName: o.customerName, orderCount: 0, orderValue: 0 });
          }
          const c = byCustomer.get(key);
          c.orderCount += 1;
          c.orderValue += value;
        });

        const installationByKey = new Map((installationReceipts || []).map((r) => [r.groupKey, num(r.totalAmount)]));
        const allByKey = new Map((allReceipts || []).map((r) => [r.groupKey, num(r.totalAmount)]));

        const customers = [...byCustomer.values()].map((c) => {
          const collected = installationByKey.get(c.key) || 0;
          return {
            ...c,
            collected,
            balance: Math.max(0, c.orderValue - collected),
            otherReceipts: Math.max(0, (allByKey.get(c.key) || 0) - collected),
          };
        });

        setState({ customers, unlinked, isLoading: false, isError: false });
      } catch (e) {
        if (thisRequest === requestId.current) {
          setState({ customers: [], unlinked: { count: 0, amount: 0 }, isLoading: false, isError: true });
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchId, skip]);

  return state;
};
